import React, { useState } from 'react';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { SlimCard } from '../../../z40-uicomp/centered-card';
import { HeadingSmall } from 'baseui/typography';
import { SpaceTopAndBottom } from '../../../z40-uicomp/layout';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { AppMessageDialog } from '../AppMessageDialog';

interface FormData {
  C256_LearningEnvironmentId: string;
  C256_HomeType: string;
  C256_LearningSpace: string;
  C256_LightingAndNoise: string;
  C256_AccessToOutdoors: string;
  C256_StorageAndMaterials: string;
  C256_InternetConnection: string;
  C256_Technology: string;
  C256_LocationSetting: string;
  C256_Postcode: string;
  C256_LocalCultureAndLanguage: string;
  C256_NearbyResources: string;
  C256_Transport: string;
  C256_NeighborhoodSafetyAndFreedom: string;
}

export function Environment() {
  const viewStates = {
    loading: 0,
    ready: 1,
    submitting: 2
  };

  const [viewState, setViewState] = useState(viewStates.loading);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [savedDialogOpen, setSavedDialogOpen] = useState(false);

  const [getFormData] = useLazyQuery("C257v1_GetC256FormData", {});
  const [submitCommand] = useLazyCommand("C256v1_SaveLearningEnvironment");

  useEvent({
    eventName: "E253v1_GotC256FormData",
    onEventReceived: (event: any) => {
      // The event contains e000_FieldData with flattened field structure (camelCase in browser)
      const fieldData = event.e000_FieldData || [];
      
      // Helper to find field by name (case-insensitive match)
      const getField = (fieldName: string) => {
        return fieldData.find((f: any) => 
          f.name?.toLowerCase() === fieldName.toLowerCase()
        );
      };

      // Find the C256 fields - field names may be in camelCase or PascalCase format
      const learningEnvironmentIdField = getField('c256_LearningEnvironmentId');
      const homeTypeField = getField('c256_HomeType');
      const learningSpaceField = getField('c256_LearningSpace');
      const lightingAndNoiseField = getField('c256_LightingAndNoise');
      const accessToOutdoorsField = getField('c256_AccessToOutdoors');
      const storageAndMaterialsField = getField('c256_StorageAndMaterials');
      const internetConnectionField = getField('c256_InternetConnection');
      const technologyField = getField('c256_Technology');
      const locationSettingField = getField('c256_LocationSetting');
      const postcodeField = getField('c256_Postcode');
      const localCultureAndLanguageField = getField('c256_LocalCultureAndLanguage');
      const nearbyResourcesField = getField('c256_NearbyResources');
      const transportField = getField('c256_Transport');
      const neighborhoodSafetyAndFreedomField = getField('c256_NeighborhoodSafetyAndFreedom');

      setFormData({
        C256_LearningEnvironmentId: learningEnvironmentIdField?.initialValue || '',
        C256_HomeType: homeTypeField?.initialValue || '',
        C256_LearningSpace: learningSpaceField?.initialValue || '',
        C256_LightingAndNoise: lightingAndNoiseField?.initialValue || '',
        C256_AccessToOutdoors: accessToOutdoorsField?.initialValue || '',
        C256_StorageAndMaterials: storageAndMaterialsField?.initialValue || '',
        C256_InternetConnection: internetConnectionField?.initialValue || '',
        C256_Technology: technologyField?.initialValue || '',
        C256_LocationSetting: locationSettingField?.initialValue || '',
        C256_Postcode: postcodeField?.initialValue || '',
        C256_LocalCultureAndLanguage: localCultureAndLanguageField?.initialValue || '',
        C256_NearbyResources: nearbyResourcesField?.initialValue || '',
        C256_Transport: transportField?.initialValue || '',
        C256_NeighborhoodSafetyAndFreedom: neighborhoodSafetyAndFreedomField?.initialValue || '',
      });
      setViewState(viewStates.ready);
    }
  });

  useEvent({
    eventName: "E252v1_LearningEnvironmentSaved",
    onEventReceived: () => {
      setViewState(viewStates.ready);
      setSavedDialogOpen(true);
    }
  });

  const handleSavedDialogOpenChange = (open: boolean) => {
    setSavedDialogOpen(open);
    if (!open) {
      location.href = '#/app/profiles/students';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    setViewState(viewStates.submitting);
    submitCommand({
      $type: "C256v1_SaveLearningEnvironment",
      ...formData
    });
  };

  const updateField = (field: keyof FormData, value: string) => {
    if (formData) {
      setFormData({ ...formData, [field]: value });
    }
  };

  switch (viewState) {
    case viewStates.loading:
      return (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-action-blue"></div>
        </div>
      );
    
    case viewStates.ready:
    case viewStates.submitting:
      if (!formData) return null;

  return (
    <>
    <div className="space-y-4">
      <SpaceTopAndBottom>
        <SlimCard>
          <div className="space-y-6">
            <div className="text-center">
              <HeadingSmall>Learning Environment</HeadingSmall>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Home & Physical Environment Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Home & Physical Environment</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="homeType">Type of Home</Label>
                  <Input
                    id="homeType"
                    value={formData.C256_HomeType}
                    onChange={(e) => updateField('C256_HomeType', e.target.value)}
                    placeholder="e.g., apartment, townhouse, detached house, rural farm"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="learningSpace">Learning Space</Label>
                  <textarea
                    id="learningSpace"
                    value={formData.C256_LearningSpace}
                    onChange={(e) => updateField('C256_LearningSpace', e.target.value)}
                    placeholder="Does the child have a dedicated desk or quiet study area?"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lightingAndNoise">Lighting & Noise</Label>
                  <textarea
                    id="lightingAndNoise"
                    value={formData.C256_LightingAndNoise}
                    onChange={(e) => updateField('C256_LightingAndNoise', e.target.value)}
                    placeholder="Natural light, artificial lighting, background noise levels"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accessToOutdoors">Access to Outdoors</Label>
                  <textarea
                    id="accessToOutdoors"
                    value={formData.C256_AccessToOutdoors}
                    onChange={(e) => updateField('C256_AccessToOutdoors', e.target.value)}
                    placeholder="Garden, backyard, park nearby, balcony, etc."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="storageAndMaterials">Storage & Materials</Label>
                  <textarea
                    id="storageAndMaterials"
                    value={formData.C256_StorageAndMaterials}
                    onChange={(e) => updateField('C256_StorageAndMaterials', e.target.value)}
                    placeholder="Access to stationery, whiteboard, art supplies, books"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="internetConnection">Internet Connection</Label>
                  <textarea
                    id="internetConnection"
                    value={formData.C256_InternetConnection}
                    onChange={(e) => updateField('C256_InternetConnection', e.target.value)}
                    placeholder="Speed, stability, shared use"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="technology">Technology</Label>
                  <textarea
                    id="technology"
                    value={formData.C256_Technology}
                    onChange={(e) => updateField('C256_Technology', e.target.value)}
                    placeholder="Desktop/laptop/tablet, printer, webcam, microphone, etc."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>
              </div>

              {/* Location & Community Context Section */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-semibold">Location & Community Context</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="locationSetting">Urban/Rural/Suburban Setting</Label>
                  <Input
                    id="locationSetting"
                    value={formData.C256_LocationSetting}
                    onChange={(e) => updateField('C256_LocationSetting', e.target.value)}
                    placeholder="Urban, rural, or suburban"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    id="postcode"
                    value={formData.C256_Postcode}
                    onChange={(e) => updateField('C256_Postcode', e.target.value)}
                    placeholder="Enter postcode"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="localCultureAndLanguage">Local Culture & Language</Label>
                  <textarea
                    id="localCultureAndLanguage"
                    value={formData.C256_LocalCultureAndLanguage}
                    onChange={(e) => updateField('C256_LocalCultureAndLanguage', e.target.value)}
                    placeholder="Regional dialects, cultural references, community values"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nearbyResources">Nearby Resources</Label>
                  <textarea
                    id="nearbyResources"
                    value={formData.C256_NearbyResources}
                    onChange={(e) => updateField('C256_NearbyResources', e.target.value)}
                    placeholder="Libraries, museums, sports fields, nature reserves, community centers"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="transport">Transport</Label>
                  <textarea
                    id="transport"
                    value={formData.C256_Transport}
                    onChange={(e) => updateField('C256_Transport', e.target.value)}
                    placeholder="Walking vs. driving access to these resources"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="neighborhoodSafetyAndFreedom">Neighborhood Safety & Freedom</Label>
                  <textarea
                    id="neighborhoodSafetyAndFreedom"
                    value={formData.C256_NeighborhoodSafetyAndFreedom}
                    onChange={(e) => updateField('C256_NeighborhoodSafetyAndFreedom', e.target.value)}
                    placeholder="Can the child play outside or explore locally?"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
                    disabled={viewState === viewStates.submitting}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 pb-20 sm:pb-24">
                <Button type="submit" disabled={viewState === viewStates.submitting}>
                  {viewState === viewStates.submitting ? 'Saving...' : 'Save Learning Environment'}
                </Button>
              </div>
            </form>
          </div>
        </SlimCard>
      </SpaceTopAndBottom>
    </div>
    <AppMessageDialog
      open={savedDialogOpen}
      onOpenChange={handleSavedDialogOpenChange}
      title="Saved"
      message="Your learning environment data was saved."
    />
    </>
      );
    
    default:
      return null;
  }
}